/**
 * Guided Tour Step Registry (#1559)
 *
 * Each step pairs a route with a DOM anchor (`data-tour="<id>"`) and copy.
 * Cross-route advancement is handled by TourProvider — when the current
 * route does not match `step.route`, navigation fires before joyride
 * re-resolves the target.
 *
 * Targets are looked up by attribute selector: [data-tour="<id>"].
 * If the anchor depends on club state (e.g. `data-tour="club-activities"` only
 * renders when BottomNav has a club context), mark the step `requiresClub: true`
 * so it's filtered out for users with zero clubs. Without this, Joyride fires
 * TARGET_NOT_FOUND and the tour cascades forward through steps too quickly to
 * read — the symptom being "the tour jumped to the last step" (#1601).
 */

export interface TourStep {
  /** Route to navigate to before showing this step. Use `:slug` for club placeholder. */
  route: string;
  /** data-tour attribute value on the anchor element. */
  target: string;
  /** Tooltip title (short). */
  title: string;
  /** Tooltip body copy. */
  content: string;
  /** joyride placement (default: 'bottom'). */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'auto';
  /**
   * If true, step is shown only when the user has at least one club. Use for
   * steps whose anchor only renders in a club-aware component (e.g. ActivityNavItem
   * in BottomNav, which falls back to StaticNavItem without the data-tour attribute
   * when the user is clubless).
   */
  requiresClub?: boolean;
  /**
   * If true, step is shown only when the user belongs to MORE THAN ONE club.
   * Use for anchors that only render for multi-club users (e.g. the ContextSheet
   * "Clubs" section, which is hidden for single-club users per ADR-UX-003 so they
   * never see a dead-end one-item switcher). Without this, Joyride hits
   * TARGET_NOT_FOUND for single-club users and cascades forward.
   */
  requiresMultipleClubs?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    route: '/clubs/:slug',
    target: 'club-switcher',
    title: 'Swap between multiple clubs',
    content: 'Switch between your clubs from here without leaving the page.',
    placement: 'bottom',
  },
  {
    route: '/activity/events',
    target: 'club-activities',
    title: 'Activities',
    content: "Every club's activities are filtered into past and future events. Past activities show who attended.",
    placement: 'top',
    requiresClub: true,
  },
  {
    route: '/clubs/:slug/chat',
    target: 'club-chat-entry',
    title: 'Discuss',
    content: "Each club has its own conversation space, separate from your other clubs.",
    placement: 'top',
  },
  {
    route: '/clubs/:slug/chat',
    target: 'conversation-selector',
    title: 'Switch conversations',
    content: 'Tap the room name in the header to jump between the Lobby and event rooms.',
    placement: 'bottom',
  },
  {
    route: '/clubs/:slug/chat',
    target: 'chat-rooms-list',
    title: 'Room selector',
    content: 'Each club has multiple rooms — Lobby for general chat, plus rooms for active and upcoming events. Switch between them here.',
    placement: 'center',
  },
  {
    route: '/clubs/:slug/chat',
    target: 'context-clubs',
    title: 'All your clubs, one tap',
    content: 'This same sheet switches clubs — tap another club to jump straight to its conversations.',
    placement: 'center',
    requiresMultipleClubs: true,
  },
  {
    route: '/settings',
    target: 'settings-tab',
    title: 'Settings',
    content: 'Profile, notifications, accessibility, units, support, and account — all your settings live here.',
    placement: 'top',
  },
  {
    route: '/me',
    target: 'profile-header',
    title: 'Make it yours',
    content: 'Add an avatar and update your name and personal details here.',
    placement: 'bottom',
  },
];

/**
 * Resolve `:slug` placeholder against a known club slug.
 * If no slug is available, the step is skipped by the caller.
 */
export function resolveStepRoute(route: string, clubSlug: string | null): string | null {
  if (!route.includes(':slug')) return route;
  if (!clubSlug) return null;
  return route.replace(':slug', clubSlug);
}
